import { Box, Flex, Text, Badge, Input } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppButton, AppSelect } from "../../components";
import { colors } from "../../theme";
import { showToast } from "../../redux/reducer/utilsSlice";
import { foundingEndDate, isFoundingPlan } from "../../utils/foundingOffer";
import {
  useRestaurantSubscriptions,
  useSubscriptionPlans,
  useSetSubscriptionEndDate,
  useGrantSubscription,
} from "../../services/useQueries";

// Dates are shown and entered in Oman time. A chosen end date means "active
// through that day", so it is sent as 23:59:59 Muscat time.
const OMAN = "Asia/Muscat";
const showDate = (value) =>
  new Date(value).toLocaleDateString("en-GB", { timeZone: OMAN, day: "numeric", month: "short", year: "numeric" });
const toInputDate = (value) => new Date(value).toLocaleDateString("en-CA", { timeZone: OMAN });
const endOfOmanDay = (yyyyMmDd) => new Date(`${yyyyMmDd}T23:59:59+04:00`).toISOString();

const rows = (response) => (Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []);

/**
 * A vendor's subscription, for super admins: the vendor app sells nothing
 * (app store rules), so MallBuddy starts a vendor's subscription here once it
 * is paid, or for a founding shop's free first month (utils/foundingOffer.js),
 * or moves a running subscription's end date.
 */
const SubscriptionPanel = ({ restaurantId }) => {
  const dispatch = useDispatch();
  const { data: subsResponse, isLoading } = useRestaurantSubscriptions(restaurantId);
  const { data: plansResponse } = useSubscriptionPlans();
  const setEndDate = useSetSubscriptionEndDate();
  const grant = useGrantSubscription();

  const subscriptions = rows(subsResponse);
  const plans = rows(plansResponse).filter((plan) => plan.isActive !== false);
  const now = Date.now();
  const current = subscriptions
    .filter((s) => s.status === "ACTIVE" && s.endDate)
    .sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0];
  const running = current && new Date(current.endDate).getTime() > now;
  // The most recent subscription of any status, for "Ended <date>" when none runs.
  const latest = subscriptions
    .filter((s) => s.endDate)
    .sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0];

  const [endDate, setEndDateValue] = useState("");
  const [planId, setPlanId] = useState("");
  useEffect(() => {
    setEndDateValue(running ? toInputDate(current.endDate) : "");
  }, [running, current?.endDate]);

  const today = toInputDate(now);
  const validDate = endDate && endDate > today;

  const save = () => {
    setEndDate.mutate(
      { id: current.id, endDate: endOfOmanDay(endDate) },
      { onSuccess: () => dispatch(showToast({ message: `Subscription now runs until ${showDate(endOfOmanDay(endDate))}`, type: "success" })) },
    );
  };
  const activate = () => {
    grant.mutate(
      { restaurantId, planId, endDate: endOfOmanDay(endDate) },
      { onSuccess: () => dispatch(showToast({ message: `Subscription started, active until ${showDate(endOfOmanDay(endDate))}`, type: "success" })) },
    );
  };

  let status;
  if (isLoading) status = { text: "Loading…", palette: "gray" };
  else if (running && current.cancelledAt) status = { text: `Cancelled by the shop, active until ${showDate(current.endDate)}`, palette: "orange" };
  else if (running) status = { text: `Active until ${showDate(current.endDate)}`, palette: "green" };
  else if (latest) status = { text: `Ended ${showDate(latest.endDate)}`, palette: "red" };
  else status = { text: "No subscription yet", palette: "red" };

  return (
    <Box borderTop="1px solid" borderColor={colors.disabled} pt={4}>
      <Text fontWeight="bold" color={colors.text} mb={2}>
        Subscription
      </Text>
      <Flex gap={2} align="center" wrap="wrap" mb={3}>
        <Badge colorPalette={status.palette}>{status.text}</Badge>
        {(running ? current : latest)?.plan?.name && (
          <Text color={colors.textSecondary}>{(running ? current : latest).plan.name}</Text>
        )}
      </Flex>

      {running ? (
        <Flex gap={3} align="flex-end" wrap="wrap">
          <Box flex="1" minW="180px">
            <Text fontSize="sm" color={colors.text} mb={1.5}>
              Active until (end of day, Oman time)
            </Text>
            <Input type="date" value={endDate} min={today} onChange={(e) => setEndDateValue(e.target.value)} />
          </Box>
          <AppButton onClick={save} disabled={!validDate || setEndDate.isPending}>
            Save end date
          </AppButton>
        </Flex>
      ) : (
        <Flex direction="column" gap={3}>
          <Text fontSize="sm" color={colors.textSecondary}>
            Start a subscription once the shop has paid, or for a founding shop's free first month. It runs from today
            until the date you choose.
          </Text>
          <AppSelect
            label="Plan"
            name="grantPlan"
            placeholder="Choose a plan"
            options={plans.map((plan) => ({ value: plan.id, label: plan.name }))}
            value={planId}
            onChange={(value) => {
              setPlanId(value || "");
              // Founding offer: the first month is free, so suggest one month from today.
              if (isFoundingPlan(plans.find((plan) => plan.id === value)) && !endDate) {
                setEndDateValue(foundingEndDate(today));
              }
            }}
          />
          {isFoundingPlan(plans.find((plan) => plan.id === planId)) && (
            <Text fontSize="sm" color={colors.textSecondary}>
              Founding offer: the first month is free. The end date is set to one month from today; change it only if
              you agreed something else with the shop.
            </Text>
          )}
          <Box>
            <Text fontSize="sm" color={colors.text} mb={1.5}>
              Active until (end of day, Oman time)
            </Text>
            <Input type="date" value={endDate} min={today} onChange={(e) => setEndDateValue(e.target.value)} />
          </Box>
          <AppButton onClick={activate} disabled={!planId || !validDate || grant.isPending}>
            Start subscription
          </AppButton>
        </Flex>
      )}
    </Box>
  );
};

export default SubscriptionPanel;
